import {
    createStore,
    applyMiddleware,
    compose,
} from 'redux'
import { routerMiddleware } from 'connected-react-router'
import thunk from 'redux-thunk'
import * as Sentry from '@sentry/react'

import rootReducer from './reducers/index'

import { createBrowserHistory } from 'history'
import { S_SPORTUJ_V_OSTRAVE } from '../consts'
import { INVESTMENT_STORE_KEY } from './reducers/sport-venues-front'

export const EVENTS_FRONT_ELEMENTS = ['running', 'prev', 'events']

export const DEFAULT_EVENTS_FRONT_STORE = {
    events: [],
    busy: false,
    cursor: 0,
    canLoadMore: false,
    markers: [],
    total: null,
    totalCount: 0,
    initLoad: false,
}

export const DEFAULT_FILTERS_STORE = {
    name: '',
    isFiltering: false,
}

export const DEFAULT_ENTITY_HEADING = {
    title: null,
    description: null,
}

export const DEFAULT_AUTH_FORMS = {
    redirectUrl: null,
    loginForm: {
        data: {
            email: '',
            password: '',
        },
        errors: null,
        errorTooManyInvalidCredentials: false,
        responseData: null,
        isPending: false,
    },
    signupForm: {
        data: {
            name: '',
            surname: '',
            email: '',
            password: '',
        },
        errors: null,
        responseData: null,
        isPending: false,
        emailCheckResponseId: null,
        responsiveCurrentStep: 1,
    },
    forgottenPasswordForm: {
        data: {
            email: '',
        },
        errors: null,
        responseData: null,
        isPending: false,
    },
    lastLoggedUserForm: {
        data: {
            name: '',
            surname: '',
            email: '',
            avatar: '',
            password: '',
        },
        loggedWith: '',
    },
}

export const DEFAULT_ON_BOARDING = {
    currentStep: 0,
    sports: null,
    fetchSportsIsPending: false,
    finished: false,
    userSport: null,
    userData: {
        sport: null,
        relationToSport: [],
        place: null,
        birthday: null,
        achievementSport: null,
        achievementText: null,
    },
}

export const DEFAULT_NOT_FOUNDED_ROUTE = {
    frontRoutes: false,
    adminRoutes: false,
    superAdminRoutes: false,
    translatedRoutes: false,
    globalRoutes: false,
}

export const DEFAULT_PROFILE = {
    sportnections: [],
    photos: [],
    photosLastEvaluatedKey: null,
    busyPhotos: false,
}

export const DEFAULT_IMAGE_CROPPER = {
    imageSrc: null,
    crop: {
        x: 0,
        y: 0,
    },
    zoom: 1,
    aspect: 1,
    croppedAreaPixels: null,
    croppedImage: null,
    isCropping: false,
    busy: false,
    error: null,
}

/**
 * Returns item from local storage
 * @param {string} key
 * @returns {null|object}
 */
export const getItemFromLocalStorage = key => localStorage.getItem(key) && localStorage.getItem(key) !== 'undefined' ? JSON.parse(localStorage.getItem(key)) : null

/**
 * Make session storage as async
 *
 * @type {{removeItem: (function(*=): Promise<void>), getItem: (function(*=): Promise<string>), setItem: (function(*=, *=): Promise<void>)}}
 */
export const asyncSessionStorage = {
    setItem: (key, value) => Promise.resolve().then(() => {
        sessionStorage.setItem(key, value)
    }),
    getItem: key => Promise.resolve().then(() => sessionStorage.getItem(key)),
    removeItem: key => Promise.resolve().then(() => sessionStorage.removeItem(key)),
}

/**
 * Make local storage as async
 *
 * @type {{removeItem: (function(*=): Promise<void>), getItem: (function(*=): Promise<string>), setItem: (function(*=, *=): Promise<void>)}}
 */
export const asyncLocalStorage = {
    setItem: (key, value) => Promise.resolve().then(() => {
        localStorage.setItem(key, value)
    }),
    getItem: key => Promise.resolve().then(() => localStorage.getItem(key)),
    removeItem: key => Promise.resolve().then(() => localStorage.removeItem(key)),
}

export const initialState = {
    auth: {
        user: null,
        tokens: getItemFromLocalStorage('sportnect.tokens'),
        error: null,
        storeTokens: null,
        cognitoStoreTokens: getItemFromLocalStorage('cognito.tokens'),
        cognitoUserAttributes: getItemFromLocalStorage('cognito.user.attributes'),
        logoutRedirectPage: null,
        fetchingAuthedUser: false,
        isOnBoardingCompletly: true,
        busyAWS: false,
    },
    ui: {
        busySections: {
            login: false,
            news: false,
            entities: false,
            sportVenue: false,
            group: false,
            notificationsCheckAccess: false,
            groupMembersMeta: false,
            event: false,
        },
    },
    app: {
        notifications: {
            data: [],
            active: false,
            cursor: 0,
            lockPopup: false,
        },
        login: {
            show: false,
            modalVisible: false,
            modalText: null,
            forceLoginPage: false,
            successAction: null,
            fetchingLogout: false,
            loginAction: null,
            authModalOpen: false,
        },
        cheer: {
            modalVisible: false,
            modalText: null,
            successAction: null,
            busy: false,
            isCheer: false,
            entity: null,
        },
        register: { postMsg: null },
        attendance: {
            busy: false,
            isChanged: false,
            data: [],
        },
        invitation: {
            busy: false,
            isChanged: false,
            data: [],
        },
        roster: {
            positions: [],
        },
        events: {
            ids: [],
            data: {},
            cursor: 0,
            filters: {
                filters: null,
            },
        },
        news: {
            ids: [],
            data: {},
            fullActuality: null,
            cursor: 0,
            actualityId: null,
            statusCode: null,
            filter: null,
        },
        notFilteredNews: {
            ids: [],
            data: {},
            fullActuality: null,
            cursor: 0,
            actualityId: null,
            statusCode: null,
        },
        newsCategories: {},
        newsTags: {},
        newsListTitle: '',
        slides: {
            ids: [],
            data: {},
        },
        menu: {
            isResponsive: false,
        },
        superAdmin: {
            system: S_SPORTUJ_V_OSTRAVE,
        },
        entities: {
            events: null,
            groups: null,
            sportVenues: null,
            users: null,
        },
        loadingMap: true,
        descriptionHeightSize: 0,
    },
    messenger: {
        messages: {},
        threads: {},
        users: [],
        participants: [],
        unreadMessagesCount: 0,
        lastSeen: null,
        busySend: false,
    },
    eventsAdmin: {
        multipleSelect: [],
    },
    groupsAdmin: {
        groups: [],
    },
    eventsFront: {
        online: DEFAULT_EVENTS_FRONT_STORE,
        running: DEFAULT_EVENTS_FRONT_STORE,
        prev: DEFAULT_EVENTS_FRONT_STORE,
        events: DEFAULT_EVENTS_FRONT_STORE,
        tournaments: DEFAULT_EVENTS_FRONT_STORE,
    },
    sportVenuesFront: {
        sportVenues: [],
        busy: false,
        cursor: 0,
        limit: 30,
        markers: [],
        total: null,
        totalCount: 0,
        isFilter: false,
        types: {
            [INVESTMENT_STORE_KEY]: {   // investment
                apply: false,
                investmentsData: [],
                markers: [],
                activeMarkerVenue: null,
                busy: false,
                filterHashtags: null,
            },
        },
    },
    users: {
        searchString: null,
        data: [],
        busy: false,
        cursor: 0,
        canLoadMore: true,
    },
    filters: {
        ...DEFAULT_FILTERS_STORE,
        isWhitelistedRoute: false,
    },
    authForms: {
        ...DEFAULT_AUTH_FORMS,
    },
    onBoarding: {
        ...DEFAULT_ON_BOARDING,
    },
    profile: {
        ...DEFAULT_PROFILE,
    },
    errors: {
        apiError: null,
    },
    notFoundRoutes: {
        ...DEFAULT_NOT_FOUNDED_ROUTE,
    },
    pageHeader: {
        ...DEFAULT_ENTITY_HEADING,
    },
    sports: {
        busy: false,
        data: null,
    },
    systemRedirect: {
        backLink: null,
        forwardLink: null,
        isLoginRedirect: false,
    },
    photoUpload: {
        photoBase64: null,
        rootType: null,
        rootId: 0,
    },
    locale: {
        lang: 'cs',
    },
    imageCropper: {
        ...DEFAULT_IMAGE_CROPPER,
    },
    apiChecker: {
        status: null,
    },
    showTournamentsBanner: {
        status: true,
        hideTournamentsBannerForEvents: [],
    },
    sportMeta: {},
}

const history = createBrowserHistory()

const appVersion = window?.APP_CONFIG?.REACT_APP_VERSION
const isDev = appVersion === '@local' || appVersion === '@dev'

const sentryReduxEnhancer = Sentry.createReduxEnhancer()

const store = createStore(
    rootReducer(history),
    initialState,
    compose(
        applyMiddleware(
            routerMiddleware(history),
            thunk
        ),
        sentryReduxEnhancer,
        isDev && window.__REDUX_DEVTOOLS_EXTENSION__ ? window.__REDUX_DEVTOOLS_EXTENSION__() : method => (method)
    ),
)

export default store
